import { Fragment } from 'react';
import { Button, Grid, Space, Spinner } from '@looker/components';
import type { UseQueryResult } from '@tanstack/react-query';
import Highcharts from 'highcharts/highstock';

import { GetChartData } from '../api/queries/get-chart-data';
import { ChartType } from '../entities/chart-type';
import { type Filters, type RequestFilters } from '../entities/filters';
import type { Node } from '../entities/node';

import { Chart } from './chart';
import { EmptyChartState } from './empty-chart-state';

export type DataExplorerResultProps = {
  requestFilters: RequestFilters;
  requests: { node: Node; query: UseQueryResult<GetChartData.Output> }[];
  onUpdateFilters: (updated: Partial<Filters>) => void;
};

const chartTypesOrder: ChartType[] = [
  ChartType.VOLTAGE,
  ChartType.CURRENT,
  ChartType.SOC,
  ChartType.TEMPERATURE,
  ChartType.CELL_VOLTAGE,
];

export function DataExplorerResult({
  requests,
  requestFilters,
  onUpdateFilters,
}: DataExplorerResultProps) {
  // If all queries are errored, show an error message
  if (requests.every(({ query }) => query.isError)) {
    return <div>Something went wrong</div>;
  }

  // If all queries are loading, show a global spinner
  if (requests.every(({ query }) => query.isLoading || query.isError)) {
    return (
      <Grid columns={1} gap="u2" className="dataExplorerWrapper">
        <Space justify="center" height="80vh" align="center">
          <Spinner />
        </Space>
      </Grid>
    );
  }

  function handleResetZoom() {
    for (const chart of Highcharts.charts) {
      if (!chart) {
        continue;
      }

      const axis = chart.xAxis.at(0);

      if (!axis) {
        continue;
      }

      axis.setExtremes(undefined, undefined, true);
    }
  }

  const anyChartHasData = requests.some(
    ({ query }) =>
      !query.isLoading &&
      !query.isError &&
      Object.values(query.data ?? {}).some(
        (arr) => Array.isArray(arr) && arr.length > 0,
      ),
  );
  const allChartsEmpty =
    !anyChartHasData &&
    requests.every(({ query }) => !query.isLoading && !query.isError);

  return (
    <div className="px-[60px] py-[40px]" data-testid="data-explorer-results">
      {anyChartHasData ? (
        <Space mb="u5" justify="end">
          <Button className="zoom-btn" onClick={handleResetZoom}>
            Reset Zoom
          </Button>
        </Space>
      ) : null}
      {allChartsEmpty ? (
        <div className="mb-5">
          <EmptyChartState
            chartType={ChartType.VOLTAGE}
            from={requestFilters.from}
            to={requestFilters.to}
            filterNoise={requestFilters.filterNoise}
            onUpdateFilters={onUpdateFilters}
          />
        </div>
      ) : null}
      <div className="space-y-5">
        {chartTypesOrder.map((chartType) => (
          <Fragment key={chartType}>
            {requestFilters.chartTypes.includes(chartType) ? (
              <Chart
                chartType={chartType}
                from={requestFilters.from}
                to={requestFilters.to}
                filterNoise={requestFilters.filterNoise}
                requests={requests}
                onUpdateFilters={onUpdateFilters}
              />
            ) : null}
          </Fragment>
        ))}
      </div>
    </div>
  );
}
